import { useState } from 'react';
import { AxiosError } from 'axios';
import { apiClient } from '../../../api/client';
import { SERVER_URL } from '../../../config/env';
import { RecipeMake } from '../items/recipe_make_card';
import { useApiList } from '../../../hooks/useApiList';

// define Chef structure
export interface ChefInfo {
  userId: string;
  username: string;
}

export type RecipeInfo = {
  title: string;
  id: string;
  createdBy: string;
  chef?: ChefInfo;
  description: string;
  difficulty: number;
  time: string;
  dateAdded: Date;
  dateMade: Date;
  images: string[];
  ingredients: { name: string; quantity: string; }[];
  comments: RecipeMake[];
  instructions: string[];
};

export const useLoadRecipes = () => {
  const [serverError, setServerError] = useState<boolean>(false);
  const { items: recipes, loading } = useApiList<RecipeInfo>({
    request: () => apiClient.get(`/api/recipes`),
    mapData: (data) =>
      data
        .map((recipe: any) => ({
          title: recipe.title,
          createdBy: recipe.createdBy,
          id: recipe._id,
          chef: recipe.chef ? { userId: recipe.chef.userId, username: recipe.chef.username } : undefined,
          description: recipe.description,
          dateAdded: new Date(recipe.dateAdded),
          dateMade: new Date(recipe.dateMade),
          time: recipe.time,
          difficulty: recipe.difficulty,
          images: recipe.images.map((filename: string) =>
            `${SERVER_URL}/api/recipes/images/${recipe._id}/${filename}`
          ),
          ingredients: recipe.ingredients,
          comments: recipe.comments.map((comment: any) => ({
            id: comment._id,
            author: comment.author,
            date: new Date(comment.date),
            comment: comment.comment,
            rating: comment.rating,
            isConfirmed: comment.confirmed,
            image: comment.image,
            userId: comment.user,
          })),
          instructions: recipe.instructions,
        }))
        .sort((a: RecipeInfo, b: RecipeInfo) => b.dateAdded.getTime() - a.dateAdded.getTime()),
    onError: (error, { setItems, setLoading }) => {
      const axiosError = error as AxiosError;
      if (axiosError.response && axiosError.response.status === 404) {
        setItems([]);
        setLoading(false);
        console.log("No recipes found.");
      } else {
        console.error("Error fetching recipes:", axiosError);
        setServerError(true);
        setLoading(false);
      }
    },
  });

  return { recipes, loading, serverError };
};
